import {
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Select,
  Stack,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
  useSteps,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { Service } from 'shared/models/service';
import { documentMasks } from 'shared/utils/maskOptions';
import { useCreateResellerContractModalContext } from './ModalContext';

const steps = [{ title: 'Contrato' }, { title: 'Instalação' }];

interface Props {
  isOpen: boolean;
  onClose(): void;
}

const CreateResellerContractModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const ctx = useCreateResellerContractModalContext();
  const { activeStep, setActiveStep } = useSteps({
    index: 0,
    count: steps.length,
  });

  useEffect(() => {
    setActiveStep(ctx.step);
  }, [ctx.step, setActiveStep]);

  const paymentDays = [5, 10, 15, 20];
  const [dayTip, setDayTip] = useState('');

  const updateDayTip = useCallback(() => {
    const today = new Date().getDate();
    const payDate = new Date();
    payDate.setDate(ctx.paymentDay);
    if (ctx.paymentDay < today) {
      payDate.setMonth(payDate.getMonth() + 1);
    }
    setDayTip(
      `Primeira cobrança dia ${payDate.toLocaleString('pt', {
        day: 'numeric',
        month: 'long',
      })}`,
    );
  }, [ctx.paymentDay]);

  useEffect(() => {
    updateDayTip();
  }, [updateDayTip]);

  const handleContractNext = () => {
    if (ctx.validateContractStep()) {
      ctx.nextStep();
    }
  };

  const toggleService = (service: Service) => {
    if (!service.id) return;
    if (ctx.selectedServiceIds.includes(service.id)) {
      ctx.removeService(service.id);
    } else {
      ctx.addService(service.id);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent pos="relative">
        <DrawerCloseButton />
        <DrawerHeader>
          Novo Contrato - Revenda
          <Divider my="1rem" />
          <Stepper index={activeStep} mt="1rem">
            {steps.map((s, idx) => (
              <Step key={s.title}>
                <StepIndicator
                  cursor={ctx.step > idx ? 'pointer' : 'default'}
                  onClick={() => ctx.step > idx && ctx.setStep(idx)}
                >
                  <StepStatus
                    complete={<StepIcon />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <Box
                  flexShrink="0"
                  cursor={ctx.step > idx ? 'pointer' : 'default'}
                  onClick={() => ctx.step > idx && ctx.setStep(idx)}
                >
                  <StepTitle>{s.title}</StepTitle>
                </Box>
                {idx < steps.length - 1 && <StepSeparator />}
              </Step>
            ))}
          </Stepper>
        </DrawerHeader>

        {ctx.step === 0 && (
          <>
            <DrawerBody>
              <Stack spacing="1.5rem">
                <HStack spacing="1rem" wrap="wrap">
                  <FormControl flexBasis="45%">
                    <FormLabel fontSize="0.8rem" color="gray.500">
                      Método de pagamento
                    </FormLabel>
                    <Input value="Cartão de Crédito" isReadOnly bg="gray.50" />
                  </FormControl>
                  <FormControl flexBasis="45%">
                    <FormLabel fontSize="0.8rem" color="gray.500">
                      Tipo de contrato
                    </FormLabel>
                    <Input value="Revenda" isReadOnly bg="gray.50" />
                  </FormControl>
                </HStack>

                <HStack spacing="1rem" wrap="wrap">
                  <FormControl
                    isRequired
                    isInvalid={!!ctx.contractErrors.due_date}
                    flexBasis="45%"
                  >
                    <FormLabel fontSize="0.8rem" color="gray.500">
                      Dia do vencimento
                    </FormLabel>
                    <Select
                      value={ctx.paymentDay}
                      onChange={(e) =>
                        ctx.setPaymentDay(Number(e.target.value))
                      }
                    >
                      {paymentDays.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </Select>
                    <FormHelperText>{dayTip}</FormHelperText>
                    {ctx.contractErrors.due_date && (
                      <FormErrorMessage>
                        {ctx.contractErrors.due_date}
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl flexBasis="45%">
                    <FormLabel fontSize="0.8rem" color="gray.500">
                      Periodicidade
                    </FormLabel>
                    <HStack>
                      <Button
                        size="sm"
                        variant={
                          ctx.periodicity === 'monthly' ? 'solid' : 'outline'
                        }
                        colorScheme="yooga"
                        onClick={() => ctx.setPeriodicity('monthly')}
                      >
                        Mensal
                      </Button>
                      <Button
                        size="sm"
                        variant={
                          ctx.periodicity === 'yearly' ? 'solid' : 'outline'
                        }
                        colorScheme="yooga"
                        onClick={() => ctx.setPeriodicity('yearly')}
                      >
                        Anual
                      </Button>
                    </HStack>
                  </FormControl>
                </HStack>

                <FormControl
                  isRequired
                  isInvalid={!!ctx.contractErrors.services}
                >
                  <FormLabel fontSize="0.8rem" color="gray.500">
                    Serviços
                  </FormLabel>
                  {ctx.services.length === 0 ? (
                    <Text color="gray.400" fontSize="sm">
                      Nenhum serviço disponível para esta periodicidade
                    </Text>
                  ) : (
                    <Stack
                      spacing="0.5rem"
                      maxH="300px"
                      overflowY="auto"
                      border="1px"
                      borderColor="gray.200"
                      rounded="md"
                      p="0.75rem"
                    >
                      {ctx.services.map((service: Service) => (
                        <HStack
                          key={service.id}
                          p="0.5rem"
                          rounded="md"
                          _hover={{ bg: 'gray.50' }}
                          justify="space-between"
                        >
                          <HStack>
                            <Checkbox
                              isChecked={ctx.selectedServiceIds.includes(
                                service.id as number,
                              )}
                              onChange={() => toggleService(service)}
                              colorScheme="yooga"
                            />
                            <Text fontSize="sm">{service.name}</Text>
                          </HStack>
                          <Badge colorScheme="green" fontSize="xs">
                            {service.priceString}
                          </Badge>
                        </HStack>
                      ))}
                    </Stack>
                  )}
                  {ctx.contractErrors.services && (
                    <FormErrorMessage>
                      {ctx.contractErrors.services}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter gap="1rem">
              <Button variant="outline" onClick={onClose} flex="1">
                Cancelar
              </Button>
              <Button colorScheme="yooga" onClick={handleContractNext} flex="1">
                Próximo
              </Button>
            </DrawerFooter>
          </>
        )}

        {ctx.step === 1 && (
          <>
            <DrawerBody>
              <Flex columnGap="5%" rowGap="1rem" wrap="wrap">
                <FormControl
                  isRequired
                  isInvalid={!!ctx.installationErrors.document}
                >
                  <FormLabel fontSize="0.8rem" color="gray.500">
                    Documento para login
                  </FormLabel>
                  <Input
                    as={IMaskInput}
                    placeholder="000.000.000-00"
                    mask={documentMasks}
                    value={ctx.document}
                    onAccept={(value: string) => ctx.setDocument(value)}
                    type="text"
                  />
                  {ctx.installationErrors.document && (
                    <FormErrorMessage>
                      {ctx.installationErrors.document}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={!!ctx.installationErrors.password}
                >
                  <FormLabel fontSize="0.8rem" color="gray.500">
                    Senha
                  </FormLabel>
                  <Input
                    autoComplete="new-password"
                    onChange={(e) => ctx.setPassword(e.target.value)}
                    type="password"
                    value={ctx.password}
                  />
                  {ctx.installationErrors.password && (
                    <FormErrorMessage>
                      {ctx.installationErrors.password}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={!!ctx.installationErrors.confirmPassword}
                >
                  <FormLabel fontSize="0.8rem" color="gray.500">
                    Confirme a senha
                  </FormLabel>
                  <Input
                    autoComplete="new-password"
                    onChange={(e) => ctx.setConfirmPassword(e.target.value)}
                    type="password"
                    value={ctx.confirmPassword}
                  />
                  {ctx.installationErrors.confirmPassword && (
                    <FormErrorMessage>
                      {ctx.installationErrors.confirmPassword}
                    </FormErrorMessage>
                  )}
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={!!ctx.installationErrors.negocio}
                >
                  <FormLabel fontSize="0.8rem" color="gray.500">
                    Tipo do estabelecimento
                  </FormLabel>
                  <Select
                    value={ctx.negocio}
                    onChange={(e) => ctx.setNegocio(e.target.value)}
                    placeholder="Selecione o tipo do estabelecimento"
                  >
                    <option value="Outros">Em branco</option>
                    <option value="Restaurante">Restaurante</option>
                    <option value="Hamburgueria">Lanchonete</option>
                    <option value="Hamburgueria">Hamburgueria</option>
                    <option value="Doceria">Doceria</option>
                    <option value="Açaiteria">Açaiteria</option>
                    <option value="Pizzaria">Pizzaria</option>
                    <option value="Bar / Petiscaria">Bar / Petiscaria</option>
                    <option value="Saladeria">Saladeria</option>
                    <option value="Distribuidora de Bebidas">
                      Distribuidora de Bebidas
                    </option>
                    <option value="Padaria / Cafeteria">Padaria</option>
                    <option value="Padaria / Cafeteria">Cafeteria</option>
                    <option value="Sorveteria">Sorveteria</option>
                    <option value="Comida Oriental">Comida Oriental</option>
                    <option value="Neutro">Outros</option>
                  </Select>
                  {ctx.installationErrors.negocio && (
                    <FormErrorMessage>
                      {ctx.installationErrors.negocio}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Flex>
            </DrawerBody>
            <DrawerFooter gap="1rem">
              <Button variant="outline" onClick={ctx.previousStep} flex="1">
                Voltar
              </Button>
              <Button
                colorScheme="yooga"
                onClick={ctx.handleSubmit}
                flex="1"
                isDisabled={ctx.loading}
              >
                Criar Contrato
              </Button>
            </DrawerFooter>
          </>
        )}

        {ctx.loading && (
          <Flex
            pos="absolute"
            top="0"
            left="0"
            w="100%"
            h="100%"
            bg="gray.100"
            zIndex="1"
            align="center"
            justify="center"
          >
            <CircularProgress isIndeterminate color="yooga.400" />
          </Flex>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default CreateResellerContractModal;
